import * as amplitude from '@amplitude/analytics-browser';
import { track } from '@amplitude/analytics-browser';

const AMPLITUDE_API_KEY = 'd0db2ecb553a2ac97948a358f90bd714';

// Suppress Amplitude remote config errors globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  const originalError = console.error;
  console.error = (...args) => {
    const message = args.join(' ');
    // Filter out all Amplitude remote config related errors
    if (message.includes('Failed to fetch remote configuration') ||
        message.includes('Amplitude Logger [Error]') ||
        message.includes('BrowserJoinedConfigGenerator') ||
        message.includes('Failed to fetch') ||
        // Benign React dev warning emitted when a browser wallet extension
        // injects a <script> into the page (not from our components).
        message.includes('Encountered a script tag while rendering React component')) {
      // Silently ignore these errors in development
      return;
    }
    originalError.apply(console, args);
  };
}

// Advanced user context gathering
const getUserContext = () => {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    // Page context
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: document.referrer || 'direct',
    
    // UTM parameters for campaign tracking
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
    
    // Additional tracking parameters
    gclid: urlParams.get('gclid'), // Google Ads
    fbclid: urlParams.get('fbclid'), // Facebook Ads
    msclkid: urlParams.get('msclkid'), // Microsoft Ads
    
    // Device & browser info
    user_agent: navigator.userAgent,
    screen_resolution: `${screen.width}x${screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    device_pixel_ratio: window.devicePixelRatio,
    color_depth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    
    // Connection info
    connection_type: (navigator as any).connection?.effectiveType || 'unknown',
    connection_downlink: (navigator as any).connection?.downlink || null,
    
    // Session info
    timestamp: new Date().toISOString(),
    session_start: sessionStorage.getItem('session_start') || new Date().toISOString(),
  };
};

// Enhanced referrer analysis
const analyzeReferrer = (referrer: string) => {
  if (!referrer || referrer === 'direct') {
    return {
      referrer_type: 'direct',
      referrer_domain: null,
      is_social: false,
      is_search_engine: false,
      is_paid: false,
    };
  }
  
  try {
    const refUrl = new URL(referrer);
    const domain = refUrl.hostname.toLowerCase();
    
    // Social media platforms
    const socialPlatforms = [
      'facebook.com', 'twitter.com', 'x.com', 'linkedin.com', 'instagram.com',
      'youtube.com', 'tiktok.com', 'reddit.com', 'discord.com', 'telegram.org',
      'github.com', 'medium.com'
    ];
    
    // Search engines
    const searchEngines = [
      'google.com', 'bing.com', 'yahoo.com', 'duckduckgo.com', 'yandex.com',
      'baidu.com', 'ask.com', 'aol.com'
    ];
    
    const isSocial = socialPlatforms.some(platform => domain.includes(platform));
    const isSearchEngine = searchEngines.some(engine => domain.includes(engine));
    
    return {
      referrer_type: isSocial ? 'social' : isSearchEngine ? 'search' : 'referral',
      referrer_domain: domain,
      is_social: isSocial,
      is_search_engine: isSearchEngine,
      is_paid: refUrl.searchParams.has('gclid') || refUrl.searchParams.has('fbclid'),
    };
  } catch {
    return {
      referrer_type: 'unknown',
      referrer_domain: referrer,
      is_social: false,
      is_search_engine: false,
      is_paid: false,
    };
  }
};

// Initialize Amplitude
export const initializeAmplitude = () => {
  if (typeof window !== 'undefined') {
    // Set session start timestamp
    if (!sessionStorage.getItem('session_start')) {
      sessionStorage.setItem('session_start', new Date().toISOString());
    }

    amplitude.init(AMPLITUDE_API_KEY, {
      autocapture: true,
      // Explicitly disable remote configuration to prevent fetch errors
      fetchRemoteConfig: false,
      defaultTracking: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
      },
      trackingOptions: {
        ipAddress: false, // For privacy compliance
      },
      // Configuration for better development experience
      flushIntervalMillis: process.env.NODE_ENV === 'development' ? 500 : 1000,
      flushQueueSize: 30,
      flushMaxRetries: 5,
      logLevel: process.env.NODE_ENV === 'development' ? 1 : 3, // Warn in dev, Error in prod
      useBatch: true,
    });

    // Log successful initialization in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Amplitude Analytics initialized successfully');
      console.log('📊 Remote configuration: disabled');
      console.log('🔍 Autocapture: enabled');
      console.log('🎯 Lead tracking: enabled');
      console.log('🔄 Using Browser SDK 2.0 with track() function');
    }
  }
};

// Enhanced event tracking with automatic context using Browser SDK 2.0
export const trackEvent = (eventName: string, eventProperties?: Record<string, any>) => {
  if (typeof window !== 'undefined') {
    const context = getUserContext();
    const referrerData = analyzeReferrer(context.referrer as string);
    
    // Browser SDK 2.0 uses the standalone track() function
    track(eventName, {
      ...context,
      ...referrerData,
      ...eventProperties,
    });
  }
};

// Lead scoring and qualification
export const trackLead = (leadType: 'cold' | 'warm' | 'hot', source: string, additionalProperties?: Record<string, any>) => {
  const context = getUserContext();
  const referrerData = analyzeReferrer(context.referrer as string);
  
  trackEvent('Lead Generated', {
    lead_type: leadType,
    lead_source: source,
    lead_score: leadType === 'hot' ? 100 : leadType === 'warm' ? 50 : 25,
    has_utm_params: !!(context.utm_source || context.utm_medium || context.utm_campaign),
    referrer_category: referrerData.referrer_type,
    ...additionalProperties,
  });
};

// dApp-specific tracking
export const trackDAppInteraction = (action: string, component: string, additionalProperties?: Record<string, any>) => {
  trackEvent('dApp Interaction', {
    dapp_action: action,
    dapp_component: component,
    page_type: 'dapps',
    is_developer_journey: true,
    ...additionalProperties,
  });
};

// Integration tracking for developers
export const trackIntegrationStep = (step: string, status: 'started' | 'completed' | 'failed', additionalProperties?: Record<string, any>) => {
  trackEvent('Integration Step', {
    integration_step: step,
    step_status: status,
    developer_journey: true,
    ...additionalProperties,
  });
};

// Demo interaction tracking
export const trackDemoInteraction = (demoType: string, action: string, additionalProperties?: Record<string, any>) => {
  trackEvent('Demo Interaction', {
    demo_type: demoType,
    demo_action: action,
    interaction_value: action === 'completed' ? 'high' : action === 'started' ? 'medium' : 'low',
    ...additionalProperties,
  });
};

// CTA conversion tracking
export const trackCTAConversion = (ctaText: string, ctaLocation: string, destination: string, conversionType: 'click' | 'signup' | 'demo_request') => {
  const context = getUserContext();
  const referrerData = analyzeReferrer(context.referrer as string);
  
  trackEvent('CTA Conversion', {
    cta_text: ctaText,
    cta_location: ctaLocation,
    destination_url: destination,
    conversion_type: conversionType,
    conversion_value: conversionType === 'demo_request' ? 'high' : conversionType === 'signup' ? 'medium' : 'low',
    traffic_source: referrerData.referrer_type,
    is_qualified_lead: !!(context.utm_source || referrerData.is_search_engine),
  });
};

// Content engagement tracking
export const trackContentEngagement = (contentType: string, contentName: string, engagementType: string, additionalProperties?: Record<string, any>) => {
  trackEvent('Content Engagement', {
    content_type: contentType,
    content_name: contentName,
    engagement_type: engagementType,
    engagement_depth: engagementType === 'completed' ? 'deep' : engagementType === 'scrolled' ? 'medium' : 'shallow',
    ...additionalProperties,
  });
};

// Specific tracking functions for common events
export const trackPageView = (pageName: string, additionalProperties?: Record<string, any>) => {
  trackEvent('Page View', {
    page_name: pageName,
    ...additionalProperties,
  });
};

export const trackButtonClick = (buttonName: string, location?: string, additionalProperties?: Record<string, any>) => {
  trackEvent('Button Click', {
    button_name: buttonName,
    location: location,
    ...additionalProperties,
  });
};

export const trackNavigation = (from: string, to: string) => {
  trackEvent('Navigation', {
    from_page: from,
    to_page: to,
    timestamp: new Date().toISOString(),
  });
};

export const trackFeatureUsage = (featureName: string, action: string, additionalProperties?: Record<string, any>) => {
  trackEvent('Feature Usage', {
    feature_name: featureName,
    action: action,
    ...additionalProperties,
  });
};

export const trackError = (errorMessage: string, errorLocation?: string, additionalProperties?: Record<string, any>) => {
  trackEvent('Error', {
    error_message: errorMessage,
    error_location: errorLocation,
    timestamp: new Date().toISOString(),
    ...additionalProperties,
  });
};

export const trackUserInteraction = (interactionType: string, element: string, additionalProperties?: Record<string, any>) => {
  trackEvent('User Interaction', {
    interaction_type: interactionType,
    element: element,
    timestamp: new Date().toISOString(),
    ...additionalProperties,
  });
};

// Screen/page visibility tracking
export const setupVisibilityTracking = () => {
  if (typeof window !== 'undefined') {
    let startTime = Date.now();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page became hidden
        const timeSpent = Date.now() - startTime;
        trackEvent('Page Hide', {
          time_spent_seconds: Math.round(timeSpent / 1000),
          page_url: window.location.href,
          page_title: document.title,
        });
      } else {
        // Page became visible
        startTime = Date.now();
        trackEvent('Page Visible', {
          page_url: window.location.href,
          page_title: document.title,
        });
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Track scroll depth
    let maxScrollDepth = 0;
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        
        // Track significant scroll milestones
        if ([25, 50, 75, 90].includes(scrollPercent)) {
          trackEvent('Scroll Depth', {
            scroll_percent: scrollPercent,
            page_url: window.location.href,
          });
        }
      }
    };

    // Throttled scroll listener
    let scrollTimeout: NodeJS.Timeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 100);
    });

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }
};

// Click tracking for all elements
export const setupClickTracking = () => {
  if (typeof window !== 'undefined') {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Get meaningful element identifier
      const elementId = target.id;
      const elementClass = target.className;
      const elementText = target.textContent?.trim() || '';
      const elementTag = target.tagName.toLowerCase();
      const elementRole = target.getAttribute('role');
      
      // Determine element type and context
      let elementType = elementTag;
      if (elementRole) elementType = elementRole;
      if (elementTag === 'button' || (target as HTMLInputElement).type === 'submit') elementType = 'button';
      if (elementTag === 'a') elementType = 'link';
      if (elementTag === 'input') elementType = (target as HTMLInputElement).type || 'input';
      
      // Get page context
      const pageUrl = window.location.href;
      const pageTitle = document.title;
      
      trackEvent('Element Click', {
        element_type: elementType,
        element_id: elementId || null,
        element_class: elementClass || null,
        element_text: elementText.substring(0, 100), // Limit text length
        element_tag: elementTag,
        page_url: pageUrl,
        page_title: pageTitle,
        click_x: event.clientX,
        click_y: event.clientY,
        timestamp: new Date().toISOString(),
      });
    };

    // Add click listener to document
    document.addEventListener('click', handleClick, true);

    // Cleanup function
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }
};

// Session tracking
export const trackSessionStart = () => {
  trackEvent('Session Start', {
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    screen_resolution: `${screen.width}x${screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
  });
};

// Development testing function
export const testAmplitudeConnection = () => {
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('🔍 Testing Amplitude connection...');
    trackEvent('Test Event', {
      test: true,
      timestamp: new Date().toISOString(),
      message: 'Amplitude tracking is working correctly'
    });
    console.log('✅ Amplitude test event sent successfully!');
    console.log('📊 Check your Amplitude dashboard in 5-10 minutes to see events');
  }
}; 